/*
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import { Injectable } from '@angular/core';

import { ElGetters, EL_DEFAULT_ROW_LEVEL, ElTreeGridPresentationNode } from './tree-grid.model';

@Injectable()
export class ElTreeGridDataService<T> {

    private defaultGetters: ElGetters<any, T> = {
        dataGetter: node => node.data,
        childrenGetter: d => d.children || undefined,
        expandedGetter: d => !!d.expanded,
    };

    toPresentationNodes<N>(
        nodes: N[],
        customGetters?: ElGetters<N, T>,
        level: number = EL_DEFAULT_ROW_LEVEL,
    ): ElTreeGridPresentationNode<T>[] {
        const getters: ElGetters<N, T> = { ...this.defaultGetters, ...customGetters };

        return this.mapNodes(nodes, getters, level);
    }

    private mapNodes<N>(nodes: N[], getters: ElGetters<N, T>, level: number): ElTreeGridPresentationNode<T>[] {
        const { dataGetter, childrenGetter, expandedGetter } = getters;

        return nodes.map(node => {
            const childrenNodes = childrenGetter(node);
            let children: ElTreeGridPresentationNode<T>[];
            if (childrenNodes) {
                children = this.toPresentationNodes(childrenNodes, getters, level + 1);
            }

            return new ElTreeGridPresentationNode(dataGetter(node), children, expandedGetter(node), level);
        });
    }

    flattenExpanded(nodes: ElTreeGridPresentationNode<T>[]): ElTreeGridPresentationNode<T>[] {
        return nodes.reduce((res: ElTreeGridPresentationNode<T>[], node: ElTreeGridPresentationNode<T>) => {
            res.push(node);

            if (node.expanded && node.hasChildren()) {
                res.push(...this.flattenExpanded(node.children));
            }

            return res;
        }, []);
    }

    copy(nodes: ElTreeGridPresentationNode<T>[]): ElTreeGridPresentationNode<T>[] {
        return nodes.map((node: ElTreeGridPresentationNode<T>) => {
            let children: ElTreeGridPresentationNode<T>[];
            if (node.hasChildren()) {
                children = this.copy(node.children);
            }
            return new ElTreeGridPresentationNode(node.data, children, node.expanded, node.level);
        });
    }
}
